import { useEffect, useState, useMemo } from "react";
import AppSelectList, { IDropDownOptions } from "../../../Component/Common/Components/FormValidationInputs/AppSelectList";
import { useForm } from "react-hook-form";
import { Box, Dialog, DialogActions, DialogContent, Grid, Stack } from "@mui/material";
import { CustomDialogTitle } from "../../Common/Custom/CustomDialogTitle";
import { SmallLoading } from "../../../Component/Common/Components/Loading/IntermediateLoading";
import AppTextInput from "../../../Component/Common/Components/FormValidationInputs/AppTextInput";
import { LoadingButton } from "@mui/lab";
import { MDButton } from "../../../Component/Common/Components/MDButton";
import { PayrollService } from "../../../Component/Api/Payroll/PayrollService";
import { ConnectedPayroll, TestPayrollConnection } from "../../../Component/Model/Dto/Payroll/Payroll";
import { OrganisationAtom } from "../../../State/Organisation.Atom";
import { useRecoilValue } from "recoil";
import { toast } from "react-toastify";
import { ConfirmationDialog } from "../../../Component/Common/Components/ConfirmationDialog";


import { yupResolver } from "@hookform/resolvers/yup";
import { CreatePayrollValidationSchema } from "./PayrollValidationSchema";
import { MDTypography } from "../../../Component/Common/Components/MDTypography";
import PersonalVideoIcon from '@mui/icons-material/PersonalVideo';
import { Cancel, CheckCircle, East, Storage } from "@mui/icons-material";

interface IProps {
    open: boolean;
    payrollId: string;
    closeModal: () => void;
    payrollTypesItems: IDropDownOptions[];
    authenticationTypesItems: IDropDownOptions[];
}

export const PayrollModal = ({ open, payrollId, closeModal, payrollTypesItems, authenticationTypesItems }: IProps) => {
    const { control, handleSubmit, reset, formState: { isSubmitting, isDirty }, getValues } = useForm({
        mode: 'all',
        resolver: yupResolver(CreatePayrollValidationSchema)
    });

    const [selectedAuthenticationType, setSelectedAuthenticationType] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);
    const [payrollName, setPayrollName] = useState<string>('');
    const organisation = useRecoilValue(OrganisationAtom);
    const [connectionStatus, setConnectionStatus] = useState<string>("");
    const [showConnectionStatus, setShowConnectionStatus] = useState<boolean>(false);

    useEffect(() => {
        fetchPayroll();
    }, [payrollId]);

    useEffect(() => {
        if (isDirty) {
            setConnectionStatus("");
            setShowConnectionStatus(false);
        }
    }, [isDirty]);

    const fetchPayroll = async () => {
        try {
            if (payrollId && payrollId.length > 0) {
                const payrollResult = await PayrollService.getConnectedPayroll(payrollId);
                const payrollData = payrollResult.data;
                if (payrollData) {
                    const formData = {
                        ...payrollData,
                        payrollType: payrollData.payrollTypeId,
                        authenticationType: payrollData.authenticationTypeId
                    };
                    reset(formData);
                    setSelectedAuthenticationType(payrollData.authenticationTypeId);
                    setPayrollName(payrollData.payrollName);
                }
            }
        } catch (error) {
            console.log(error);
        }
    }

    const handleDataSubmit = async (data: any) => {
        try {
            const payrollRequest: ConnectedPayroll = {
                payrollId: payrollId || undefined,
                organisationId: organisation?.OrganisationId,
                payrollName: data.payrollName,
                payrollTypeId: data.payrollType,
                authenticationTypeId: data.authenticationType,
                payrollUrl: data.payrollUrl,
                username: data.username,
                password: data.password,
                authToken: data.authToken
            };
            const result = await PayrollService.savePayroll(payrollRequest);
            if (result.status === 200) {
                toast.success(`Payroll added successfully`);
                closeCreateModal();
            }
        } catch (error) {
            console.log(error);
        }
    }

   const onDelete = async () => {
        try {
            await PayrollService.disconnectPayroll(payrollId);
            setShowConfirmationDialog(false);
            toast.success(`Payroll disconnected successfully`);
            closeCreateModal();
        } catch (error) {
            console.log(error);
        }
    }

    const closeCreateModal = () => {
        closeModal();
        reset();
    }

    const handleTestConnection = async () => {
        setShowConnectionStatus(false);
        setConnectionStatus("testing");
        try {
            const content = getValues();
            const request: TestPayrollConnection = {
                apiUrl: content.payrollUrl,
                authTypeID: content.authenticationType,
                apiKey: content.authToken,
                username: content.username ?? "",
                password: content.password ?? ""
            };

            const response = await PayrollService.testPayrollConnection(request);
            response.data !== false ? setConnectionStatus("success") : setConnectionStatus("error");
            setShowConnectionStatus(true);
        } catch (error) {
            setConnectionStatus("error");
        }
    };

    const ConnectionImage = ({}) => {
            const { statusColor, text, altColor } = useMemo(() => {
            const isSuccess = connectionStatus === "success";

            return {
                statusColor: isSuccess ? "green" : "red",
                altColor: isSuccess ? "success.main" : "error.main",
                text: isSuccess
                    ? "Successfully able to connect to your payroll"
                    : "Unable to connect to your payroll.\n\nPlease update connection details and test again.",
            };
        }, [connectionStatus]);

        if (!connectionStatus) return null;

        return (
            <Box sx={{ border: `2px solid ${statusColor}`, padding: 2, marginTop: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}>
                    <PersonalVideoIcon sx={{ width: 80, height: 80 }} />

                    <East sx={{ width: 80, height: 80 }} />

                    {connectionStatus === "success" ? (
                        <CheckCircle sx={{width: 80, height: 80}} color="success" />
                    ) : (
                        <Cancel sx={{ width: 80, height: 80, color: "red" }} />
                    )}

                    <East sx={{ width: 80, height: 80 }} />

                    <Storage sx={{ width: 80, height: 80 }} />
                </Stack>

                <MDTypography
                    variant="caption"
                    component="div"
                    sx={{
                        textAlign: "center",
                        fontWeight: 500,
                        whiteSpace: "pre-line",
                        color: altColor,
                        marginTop: 2,
                    }}
                >
                    {text}
                </MDTypography>
            </Box>
        );
    };

    return (
        <Dialog disableEnforceFocus open={open} onClose={() => closeCreateModal()} aria-labelledby="form-dialog-title">
            <CustomDialogTitle id="form-dialog-title" onClose={() => closeCreateModal()}>
                {payrollId && payrollId.length > 0 ? "View Payroll Connection" : "Connect Payroll"}
            </CustomDialogTitle>
            <DialogContent>
                <form id="payrollForm" onSubmit={handleSubmit(handleDataSubmit)}>
                    {isLoading ? <SmallLoading /> :
                        <Grid container spacing={1} mt={1}>
                            <Grid item xs={12} sm={12}>
                                <AppTextInput label='Payroll Name' name='payrollName' control={control} />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <AppSelectList
                                    label='Payroll Type'
                                    name='payrollType'
                                    control={control}
                                    items={payrollTypesItems}
                                    disabled={payrollId.length > 0}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <AppTextInput label='Payroll URL' name='payrollUrl' control={control} />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <AppSelectList
                                    label='Authentication Type (Username and Password / Auth Token)'
                                    name='authenticationType'
                                    control={control}
                                    items={authenticationTypesItems}
                                    onChange={(value) => setSelectedAuthenticationType(value)}
                                />
                            </Grid>
                            {selectedAuthenticationType === 2 && (
                                <>
                                    <Grid item xs={12} sm={12}>
                                        <AppTextInput label='Username' name='username' control={control} />
                                    </Grid>
                                    <Grid item xs={12} sm={12}>
                                        <AppTextInput label='Password' name='password' control={control} type='password' />
                                    </Grid>
                                </>
                            )}
                            <Grid item xs={12} sm={12}>
                                <AppTextInput label='Auth Token' name='authToken' control={control} />
                            </Grid>
                        </Grid>
                    }
                </form>

                <Grid item xs={12}>
                    {showConnectionStatus && <ConnectionImage />}
                </Grid>
 

            </DialogContent>
            <DialogActions>
                {payrollId && payrollId.length > 0 &&
                    <MDButton variant="outlined" color="error" style={{ marginRight: "auto" }} onClick={() => setShowConfirmationDialog(true)}>Disconnect</MDButton>
                }

                <LoadingButton variant="contained" onClick={handleSubmit(handleTestConnection)} color="secondary" loading={connectionStatus === "testing"}>
                    Test Connection
                </LoadingButton>

                <LoadingButton loading={isSubmitting} variant="contained" form="payrollForm" type="submit" color="primary" disabled={connectionStatus !== "success"}>
                    Link
                </LoadingButton>

            </DialogActions>
            {showConfirmationDialog && (
                <ConfirmationDialog
                    showDialog={showConfirmationDialog}
                    heading="Confirm Disconnect"
                    message={`Are you sure you want to disconnect ${payrollName} from your organisation? \n\nYou'll need to reconnect your payroll if you want to fetch data from it again.`}
                    handleAccept={onDelete}
                    handleCancel={() => setShowConfirmationDialog(false)}
                />
            )}
        </Dialog>
    );
}